import {ChangeEvent, memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import {Button, CardBody} from "@material-tailwind/react";
import Card from "../../../components/Card";
import {API} from "../../../types/API";
import {AxiosResponse} from "axios";
import {SchemaRole, SchemaRolesResponse} from "../../../types/Responses/RolesAndPermissions";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useErrorHandler} from "react-error-boundary";
import {useToastContext} from "../../../types/contexts/ToastContext";
import Forbidden from "../../../components/Forbidden";
import {useTranslation} from "react-i18next";
import {setTitle} from "../../../hooks/setTitle";
import FormSkeleton from "../../../components/Skeletons/FormSkeleton";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {APILink} from "../../../types/APILink";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import {useUserContext} from "../../../types/contexts/UserContext";
import {setSidebarTitle} from "../../../reducers/sidebar";
import {useDispatch} from "react-redux";

export type RoleTreeNode = SchemaRole & { children: RoleTreeNode[] };
export type ParentOption = Pick<SchemaRole, 'id' | 'name'>;

const buildRoleTree = (roles: SchemaRole[]): RoleTreeNode[] => {
    const nodeMap = new Map<number, RoleTreeNode>();
    const nodes = roles.map(role => ({...role, children: []}));

    nodes.forEach(node => nodeMap.set(node.id, node));

    const roots: RoleTreeNode[] = [];

    nodes.forEach(node => {
        if (node.parent_id === null) {
            roots.push(node);
            return;
        }
        const parent = nodeMap.get(node.parent_id);
        if (parent) {
            parent.children.push(node);
        } else {
            roots.push(node);
        }
    });

    const sortBranch = (items: RoleTreeNode[]) => {
        items.sort((a, b) => a.name.localeCompare(b.name));
        items.forEach(child => sortBranch(child.children));
    }

    sortBranch(roots);
    return roots;
}

const collectDescendants = (roles: SchemaRole[], roleId: number): Set<number> => {
    const childrenMap = roles.reduce<Map<number, number[]>>((acc, role) => {
        if (role.parent_id !== null) {
            const children = acc.get(role.parent_id) ?? [];
            children.push(role.id);
            acc.set(role.parent_id, children);
        }
        return acc;
    }, new Map());

    const visited = new Set<number>();
    const stack = [...(childrenMap.get(roleId) ?? [])];

    while (stack.length) {
        const current = stack.pop();
        if (typeof current === 'undefined' || visited.has(current))
            continue;
        visited.add(current);
        const children = childrenMap.get(current);
        if (children) {
            stack.push(...children);
        }
    }

    return visited;
}

interface RoleTreeBranchProps {
    node: RoleTreeNode;
    rootRoleId: number | null;
    editingNodeId: number | null;
    setEditingNodeId: (id: number | null) => void;
    getParentOptions: (roleId: number) => ParentOption[];
    onParentChange: (roleId: number, parentId: number) => void;
    chooseLabel: string;
    helperLabel: (name: string) => string;
}

const RoleTreeBranch = memo(function RoleTreeBranchComponent({
                                                                node,
                                                                rootRoleId,
                                                                editingNodeId,
                                                                setEditingNodeId,
                                                                getParentOptions,
                                                                onParentChange,
                                                                chooseLabel,
                                                                helperLabel
                                                            }: RoleTreeBranchProps) {
    const isRoot = rootRoleId === node.id;
    const isEditing = editingNodeId === node.id && !isRoot;
    const parentOptions = useMemo(() => getParentOptions(node.id), [getParentOptions, node.id]);

    const toggleEditing = () => {
        if (isRoot)
            return;
        setEditingNodeId(isEditing ? null : node.id);
    }

    const onSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const {value} = event.currentTarget;
        if (!value)
            return;
        const parentId = parseInt(value, 10);
        if (!Number.isNaN(parentId)) {
            onParentChange(node.id, parentId);
        }
    }

    return (
        <li>
            <div
                className={`role-node-card inline-flex items-center justify-between gap-3 px-4 py-3 rounded-xl shadow-sm border bg-white dark:bg-blue-gray-900 dark:text-gray-100 border-blue-gray-50 dark:border-blue-gray-800 ${isRoot ? 'ring-2 ring-teal-500' : ''}`}>
                <span className="font-semibold">{node.name}</span>
                {!isRoot &&
                    <button type="button"
                            onClick={toggleEditing}
                            className="text-teal-500 hover:text-teal-600 transition-colors">
                        <FontAwesomeIcon icon={solid("pen-to-square")}/>
                    </button>}
            </div>
            {isEditing &&
                <div
                    className="role-node-editor mt-3 p-4 rounded-xl bg-blue-gray-50 dark:bg-blue-gray-900/60 text-left shadow-sm border border-blue-gray-100 dark:border-blue-gray-800 max-w-xs mx-auto">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{helperLabel(node.name)}</p>
                    {parentOptions.length ?
                        <select
                            className="w-full text-sm rounded-lg border border-blue-gray-200 dark:border-blue-gray-700 bg-white dark:bg-blue-gray-900 text-blue-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                            value={node.parent_id ? node.parent_id.toString() : ''}
                            onChange={onSelectChange}
                        >
                            <option value="" disabled>{chooseLabel}</option>
                            {parentOptions.map(option =>
                                <option key={option.id} value={option.id.toString()}>{option.name}</option>
                            )}
                        </select> :
                        <p className="text-xs italic text-gray-500 dark:text-gray-400">{chooseLabel}</p>}
                </div>}
            {node.children.length > 0 &&
                <ul>
                    {node.children.map(child => (
                        <RoleTreeBranch
                            key={child.id}
                            node={child}
                            rootRoleId={rootRoleId}
                            editingNodeId={editingNodeId}
                            setEditingNodeId={setEditingNodeId}
                            getParentOptions={getParentOptions}
                            onParentChange={onParentChange}
                            chooseLabel={chooseLabel}
                            helperLabel={helperLabel}
                        />
                    ))}
                </ul>}
        </li>
    );
});

const ReportingSchema = () => {
    const {t} = useTranslation();
    const handleError = useErrorHandler();
    const dispatch = useDispatch();
    const toast = useToastContext();
    const user = useUserContext();
    const [rolesSchema, setRolesSchema] = useState<SchemaRole[]>([]);
    const [isForbidden, setIsForbidden] = useState<boolean>(false);
    const [isReady, setIsReady] = useState<boolean>(false);
    const [isInProgress, setInProgress] = useState<boolean>(false);
    const [editingRoleId, setEditingRoleId] = useState<number | null>(null);

    const rootRoleId = useMemo(() => {
        const root = rolesSchema.find(role => role.parent_id === null);
        return root ? root.id : null;
    }, [rolesSchema]);

    const sendRequest = useCallback(() => {
        API.getReportingSchemaRoles().then((response: AxiosResponse) => {
            const rolesResponse = response.data as SchemaRolesResponse;
            setRolesSchema(rolesResponse.data);
            setIsReady(true);
            setEditingRoleId(null);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_REPORTING_SCHEMA_ROLES}`, toast, handleError, setIsForbidden));
    }, [handleError, toast]);

    const getParentOptions = useCallback((roleId: number) => {
        if (!rolesSchema.length)
            return [];
        const descendants = collectDescendants(rolesSchema, roleId);
        return rolesSchema
            .filter(role => role.id !== roleId && !descendants.has(role.id))
            .map(role => ({id: role.id, name: role.name}))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [rolesSchema]);

    const changeRoleParent = useCallback((roleId: number, parentId: number) => {
        setRolesSchema(prev => prev.map(role => role.id === roleId ? {...role, parent_id: parentId} : role));
        setEditingRoleId(null);
    }, []);

    const handleSuccessResponse = (message: JSX.Element | string) => {
        toast?.show(<><FontAwesomeIcon icon={solid("circle-check")} className="mr-4"/> {message}</>, "bg-teal-500");
    }

    const submit = useCallback(() => {
        if (!rolesSchema.length)
            return;
        setInProgress(true);
        const payload = rootRoleId ? rolesSchema.filter(role => role.id !== rootRoleId) : rolesSchema;
        API.updateReportingSchemaRoles({roles: payload})
            .then(() => handleSuccessResponse(t('admin.roles.schema.updated')))
            .catch(e => ErrorHandler(e, `put${APILink.GET_REPORTING_SCHEMA_ROLES}`, toast, handleError))
            .finally(() => setInProgress(false));
    }, [rolesSchema, rootRoleId, t, toast, handleError]);

    useEffect(() => {
        setTitle(t('admin.roles.reporting_schema.title'));
        dispatch(setSidebarTitle(t('admin.roles.reporting_schema.title')));
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                sendRequest();
            }).catch();
        }
    }, [t, user, sendRequest, dispatch]);

    const helperLabel = useCallback((name: string) => t('admin.roles.schema.confirmation_title', {name}), [t]);
    const chooseLabel = t('admin.roles.choose');

    const roleTree = useMemo(() => buildRoleTree(rolesSchema), [rolesSchema]);

    const schemaUI = useMemo(() => {
        if (!isReady)
            return <FormSkeleton/>;

        if (!roleTree.length)
            return <div className="p-8 text-center text-gray-500 dark:text-gray-400">{t('admin.datatable.no_data')}</div>;

        return <>
            <div className="overflow-auto pb-8 pt-2">
                <ul className="role-tree w-full">
                    {roleTree.map(node => (
                        <RoleTreeBranch
                            key={node.id}
                            node={node}
                            rootRoleId={rootRoleId}
                            editingNodeId={editingRoleId}
                            setEditingNodeId={setEditingRoleId}
                            getParentOptions={getParentOptions}
                            onParentChange={changeRoleParent}
                            chooseLabel={chooseLabel}
                            helperLabel={helperLabel}
                        />
                    ))}
                </ul>
            </div>
            <div className="mt-12">
                <Button onClick={submit} className="text-white bg-teal-500"
                        disabled={isInProgress}>{t('admin.buttons.update')}</Button>
            </div>
        </>;
    }, [chooseLabel, changeRoleParent, editingRoleId, getParentOptions, helperLabel, isInProgress, isReady, roleTree, rootRoleId, submit, t]);

    return (
        <PageBlock>
            <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody className="overflow-auto px-0">
                    {schemaUI}
                    <Forbidden isForbidden={isForbidden}/>
                </CardBody>
            </Card>
        </PageBlock>
    );
}
export default memo(ReportingSchema);
